/**
 * The measurement boundary and the resolved ladder, published to descendants.
 *
 * A layout scope is a real element, because §7.1 branches on available content
 * width and the only honest source of that is a measured box. Features read
 * `useBreakpoint()` and get the resolution for the nearest enclosing pane, so a
 * screen nested inside a 340 px inspector cannot accidentally see the window's
 * 1400 px and render its three-pane composition.
 */

import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import type { LayoutResolution } from "./breakpoints";
import { resolveLayout } from "./breakpoints";
import { useContainerWidth } from "./containerWidth";
import { useAccessibilityTextSize } from "./textSize";

const LayoutContext = createContext<LayoutResolution | null>(null);

/**
 * The resolution for the nearest layout scope.
 *
 * Missing scope is an error rather than a default: any default is a width, and
 * a wrong width silently renders the wrong composition. The failure is loud at
 * the first render instead of subtle on one device size.
 */
export function useBreakpoint(): LayoutResolution {
  const resolution = useContext(LayoutContext);
  if (resolution === null) {
    throw new Error(
      "useBreakpoint() requires an enclosing <LayoutScope>. Wrap the surface in one, " +
        "or call useResolvedLayout() with a size you measured yourself.",
    );
  }
  return resolution;
}

/** For code that must tolerate rendering outside a scope, such as a portal root. */
export function useOptionalBreakpoint(): LayoutResolution | null {
  return useContext(LayoutContext);
}

/**
 * Resolve the ladder for a size the caller already has. Used by `LayoutScope`
 * itself, and by any component that measured its own pane with
 * `useContainerWidth` and wants the same rules without nesting another scope.
 */
export function useResolvedLayout(size: { width: number; height: number }): LayoutResolution {
  const accessibilityTextSize = useAccessibilityTextSize();
  const { width, height } = size;
  return useMemo(
    () => resolveLayout({ width, height, accessibilityTextSize }),
    [width, height, accessibilityTextSize],
  );
}

export interface LayoutScopeProps extends HTMLAttributes<HTMLElement> {
  readonly as?: ElementType;
  /**
   * Override the measured width. The shell passes nothing; test harnesses and
   * a pane whose width is already known from a fixed column pass a number, so
   * the branch is deterministic without a fake `ResizeObserver`.
   */
  readonly width?: number;
  readonly height?: number;
  readonly children?: ReactNode;
  readonly "data-testid"?: string;
}

/**
 * Until the element has been measured, the scope resolves at zero width — the
 * compact, single-column composition. That is the deliberate direction to fail
 * in: a compact layout in a wide pane reflows once, whereas a three-pane layout
 * in a narrow pane overflows the body horizontally, which §7.5 forbids outright.
 */
const UNMEASURED = 0;

export function LayoutScope({
  as,
  width,
  height,
  className,
  style,
  children,
  ...rest
}: LayoutScopeProps): ReactNode {
  const { ref, size } = useContainerWidth<HTMLElement>();
  const measuredWidth = width ?? size?.width ?? UNMEASURED;
  const measuredHeight = height ?? size?.height ?? UNMEASURED;
  const resolution = useResolvedLayout({ width: measuredWidth, height: measuredHeight });

  const Element: ElementType = as ?? "div";
  const composedClassName = className === undefined ? "ff-layout-scope" : `ff-layout-scope ${className}`;
  const composedStyle: CSSProperties | undefined = style;

  return (
    <LayoutContext.Provider value={resolution}>
      <Element
        ref={ref}
        className={composedClassName}
        style={composedStyle}
        data-ff-window-class={resolution.windowClass}
        data-ff-size-class={resolution.sizeClass}
        data-ff-compact={String(resolution.isCompact)}
        data-ff-sidebar={resolution.sidebar}
        data-ff-live-board={resolution.liveBoard}
        // Mirrors the `:root[data-ff-text-size="accessibility"]` contract that
        // `tokens.css` already uses, so feature CSS can select on the branch
        // from inside a pane without depending on the document root.
        data-ff-text-size={resolution.accessibilityTextSize ? "accessibility" : undefined}
        {...rest}
      >
        {children}
      </Element>
    </LayoutContext.Provider>
  );
}
